<template lang="pug">
  v-container(fluid)
    v-snackbar(v-model="showSnackBar" :color="snackbarColor") {{ snackBarMessage }}
      template(v-slot:action="{ attrs }")
        v-btn.justify-end(fab text v-bind="attrs" @click="showSnackBar = false")
          v-icon fal fa-times-circle

    v-row(v-if="showMobileNavigationBar" no-gutters)
      v-col.pa-0(cols="12")
        v-btn(color="primary" @click="onBackToCalendarButtonClick")
          v-icon fa fa-chevron-double-left

    v-row
      v-col.pt-0(v-if="showCalendar" xs="12" sm="12" md="3" lg="3")
        v-row
          v-select(
            outlined clear-icon="fas fa-times" append-icon="fas fa-chevron-down" dense
            label="Prevádzka"
            v-model="selectedBusiness" :items="businesses" item-value="id" item-text="name" :loading="businessLoading"
            :disabled="businesses.length == 1"
            return-object)
        v-row
          calendar-selector.pa-0.ma-0(:isoWeek.sync="calendarIsoWeek" :menuOfferCollection="weeklyOfferCollection"
            :date="date")

      v-col(v-if="showOfferContent" xs="12" sm="12" md="9" lg="9")
        DailyMenuOfferSelector.pa-0.ma-0(v-if="item && date && !fetchInProgress" :item="item" :setFreeDay="setFreeDay" :index="index"
          :getEventName="getEventName" :date="date" :itemCollection="itemCollection" :allergensList="allergensList" @update="updateData")
        v-skeleton-loader(v-else type="list-item-avatar-three-line, article, actions")

</template>

<script lang="ts">
import Vue from 'vue'
import CalendarSelector from '@/components/dailymenu/calendar/CalendarSelector.vue'
import {
  FETCH_ALLERGEN_LIST,
  FETCH_CALENDAR_EVENTS_FOR_DAY,
  FOOD_DATABASE_FETCH_FOOD_ITEMS,
} from '@/store/action-types'
import DailyMenuOffer from '@/model/dailymenu/DailyMenuOffer'
import {
  USER_LOGGED_IN
} from '@/store/getter-types'
import {Component, Prop, Watch} from "vue-property-decorator";
import DailyMenuOfferSelector from "@/components/dailymenu/detail/DailyMenuOfferSelector.vue";
import {Route} from "vue-router";
import moment from "moment";
import vuetify from "@/plugins/vuetify";
import _ from "lodash";
import {formatAsIsoWeek} from "@/services/date-time";
import CalendarEventForDay from "@/model/calendar/CalendarEventForDay";
import {StructuredMenuOfferResponseItem} from "@/types/service-response";
import {
  FetchStructuredMenuOfferPayload, FetchStructuredMenuOfferWithCancelTokenPayload,
  FilterEventsPayload, FilterEventsWithCancelTokenPayload
} from "@/types/payload";
import {CalendarEvent} from "vuetify";
import {Action} from "vuex-class";
import {CurrentUser} from "@/model/CurrentUser";
import axios, {CancelTokenSource} from "axios";
import FoodItem from "@/model/food-database/FoodItem";
import Allergen from "@/model/food-database/Allergen";
import i18n from "@/i18n";
import {usePiniaStore} from '@/store/piniaStore'
import {useDailyMenuOfferStore} from '@/store/dailyMenuOfferStore'
import BusinessModel from '@/model/menu/BusinessModel'

function redirectIfNeeded(to: Route, next: Function): void {
  try {
    const paramValueResolver: {
      [key: string]: Function
    } = {
      isoWeek: () => to.params.isoWeek || formatAsIsoWeek(new Date),
      date: () => to.params.date || moment().locale(i18n.locale).format('YYYY-MM-DD')
    }

    let paramsToCheck = ['isoWeek']

    if (vuetify.framework.breakpoint.mdAndUp) {
      paramsToCheck.push('date')
    }

    // kazdy z testovanych parametrov musi byt truethy inak every vrati false
    const redirect = !paramsToCheck.every((param: string) => to.params.hasOwnProperty(param))

    if (redirect) {
      const paramValues = paramsToCheck.map((param: string) => paramValueResolver[param] && paramValueResolver[param]())

      next({
        name: 'Dailymenu',
        replace: true,
        params: _.zipObject(paramsToCheck, paramValues)
      })

    } else {
      next()
    }
  } catch (e) {
    console.error(e)
  }
}

Component.registerHooks([
  'beforeRouteEnter',
  'beforeRouteUpdate'
])

@Component({
  components: {
    CalendarSelector,
    DailyMenuOfferSelector
  }
})
export default class DailymenuPage extends Vue {

  @Action(FETCH_CALENDAR_EVENTS_FOR_DAY) fetchCalendarEventsForDay!: { (payload: FilterEventsWithCancelTokenPayload): Promise<CalendarEventForDay[]> }
  @Action(FETCH_ALLERGEN_LIST) fetchAllergenList!: ({ (): Promise<Allergen[]> })

  @Prop({type: Date}) readonly date!: Date
  @Prop({type: String, required: true}) isoWeek!: string

  private piniaStore = usePiniaStore()
  private dailyMenuOfferStore = useDailyMenuOfferStore()

  private calendarIsoWeek: string = this.isoWeek
  private weeklyOfferCollection: DailyMenuOffer[] | null = null
  private item: DailyMenuOffer | null = null

  private events: CalendarEventForDay[] | null = null
  private setFreeDay: boolean = false
  private businesses: BusinessModel[] = []
  private selectedBusiness: BusinessModel | null = null
  private businessLoading: boolean = false
  private showSnackBar: boolean = false
  private snackbarColor: string = ''
  private snackBarMessage: string | null = null
  private fetchInProgress: boolean = false

  private cancelToken: CancelTokenSource | null = null

  private itemCollection: FoodItem[] | null = null
  private allergensList: Allergen[] | null = null
  private index: number | null= null

  get currentUser(): CurrentUser {
    return this.$store.getters[USER_LOGGED_IN]
  }

  get showMobileNavigationBar(): boolean {
    return this.$vuetify.breakpoint.smAndDown && this.showOfferContent
  }

  get showCalendar(): boolean {
    if (this.$vuetify.breakpoint.smAndDown) {
      return this.date === undefined
    }

    return true
  }

  get showOfferContent(): boolean {
    if (this.$vuetify.breakpoint.smAndDown) {
      return this.date !== undefined
    }

    return true
  }

  getCurrentEvent() {

    if (this.events) {
      const foundEvent: CalendarEventForDay | undefined = this.events.find((item: CalendarEventForDay) => moment(item.date).format('D.M.YYYY') == this.$route.params.date)
      this.setFreeDay = !!foundEvent
    }
  }

  get getEventName(): string {
    if (this.events) {
      const foundEvent: CalendarEventForDay | undefined = this.events!.find((item: CalendarEventForDay) => moment(item.date).format('D.M.YYYY') == this.$route.params.date)
      if (foundEvent && foundEvent.kind.name == 'holiday') {
        return this.$t('dailymenuPage.freeDayInformationText', {eventName: 'sú prázdniny'}).toString()
      } else if (foundEvent && foundEvent.kind.name == 'free_day') {
        return this.$t('dailymenuPage.freeDayInformationText', {eventName: 'je voľno'}).toString()
      }
    }

    return ''
  }

  @Watch('isoWeek')
  watchIsoWeek(newVal: string): void {
    this.fetchWeeklyData(newVal)
  }

  @Watch('date')
  watchDate(newVal: Date): void {

    const dateOffer = this.findCachedOfferForDate(newVal)

    if (dateOffer) {
      // FIXME: predpoklada sa ze vzdy bude zobrazeny obed
      this.item = dateOffer
    }

    if (this.events) {
      this.getCurrentEvent()
    }
  }

  @Watch('calendarIsoWeek')
  watchCalendarIsoWeek(newVal: string): void {
    if (newVal) {
      this.$router.push({
        name: 'Dailymenu',
        params: {
          isoWeek: newVal,
          date: this.date && moment(this.date).format('YYYY-MM-DD')
        }
      }).catch((e) => console.debug(e))
    }
  }

  @Watch('weeklyOfferCollection')
  watchWeeklyOfferCollection(newVal: DailyMenuOffer[] | undefined): void {
    if (newVal && newVal.length) {
      const currentDateOffer = this.findCachedOfferForDate(this.date as unknown as Date)

      if (currentDateOffer) {
        // FIXME: predpoklada sa ze vzdy bude zobrazeny obed
        this.item = currentDateOffer

      }
    }
  }

  @Watch('showOfferContent')
  watchShowOfferContent(newVal: any): void {
    if (newVal && !this.date) {
      this.$router.replace({
        name: 'Dailymenu',
        params: {
          isoWeek: this.isoWeek,
          date: moment(this.isoWeek).startOf('isoWeek').format('GGGG-MM-DD')
        }
      }).catch((e) => console.debug(e))
    }
  }

  @Watch('selectedBusiness')
  watchSelectedBusiness(newVal: BusinessModel): void {
    if (newVal) {
      this.dailyMenuOfferStore.selectedBusiness = newVal
      this.fetchWeeklyData(this.isoWeek)
    }
  }

  findCachedOfferForDate(date: Date): DailyMenuOffer | undefined {
    if (this.weeklyOfferCollection) {
      return this.weeklyOfferCollection.find((i: DailyMenuOffer) => moment(i.date).format("DD-MM-YYYY") === moment(date).format("DD-MM-YYYY")) //moment(i.date).isSame(date, 'date'))
    }
  }

  async updateData(index?: any): Promise<void> {
    this.index = index
    const startDate = moment(this.isoWeek).startOf('isoWeek').toDate()
    const endDate = moment(this.isoWeek).endOf('isoWeek').toDate()
    try {
      this.fetchInProgress = true
      this.weeklyOfferCollection = await this.fetchWeeklyOffer(startDate, endDate)
      this.snackbarColor = 'success'
      this.snackBarMessage = this.$t('valueSaved').toString()
      this.showSnackBar = true
    } catch (e) {

    } finally {
      this.fetchInProgress = false
    }
  }

  async fetchWeeklyData(isoWeek: string): Promise<void> {

    this.fetchInProgress = true

    if (this.cancelToken) {
      this.cancelToken.cancel()
    }

    this.cancelToken = axios.CancelToken.source()

    const startDate = moment(isoWeek).startOf('isoWeek').toDate()
    const endDate = moment(isoWeek).endOf('isoWeek').toDate()

    const promises = [
      await this.$store.dispatch(FOOD_DATABASE_FETCH_FOOD_ITEMS),
      this.fetchWeeklyOffer(startDate, endDate),
      this.fetchEventsData(startDate, endDate),
    ]

    const [
      foodItemCollection,
      offerCollection,
    ] = await Promise.all(promises as Promise<Object[]>[]) as [FoodItem[], DailyMenuOffer[], CalendarEvent[]]

    this.itemCollection = foodItemCollection
    this.weeklyOfferCollection = offerCollection

    this.fixDateIfNeeded()

    this.fetchInProgress = false
  }

  async fetchAllergens(): Promise<void> {
    try {
      this.fetchInProgress = true
      this.allergensList = await this.fetchAllergenList()
    } catch (e) {
      this.showSnackBar = true
      this.snackbarColor = 'error'
      this.snackBarMessage = this.$t('errors.fetchDataError').toString()
    } finally {
      this.fetchInProgress = false

    }
  }

  async fetchWeeklyOffer(dateFrom: Date, dateTo: Date): Promise<DailyMenuOffer[]> {
    this.weeklyOfferCollection = null

    let cancelToken = this.cancelToken

    let payloadWithoutCancelToken: FetchStructuredMenuOfferPayload = {
      tenantCode: this.currentUser.tenant.code,
      dateFrom,
      dateTo,
      business: this.selectedBusiness!
    }

    const response = await this.piniaStore.fetchStructuredMenuOffer({
      cancelToken: cancelToken,
      payload: payloadWithoutCancelToken
    } as FetchStructuredMenuOfferWithCancelTokenPayload)

    return response
        .map((item: StructuredMenuOfferResponseItem) => DailyMenuOffer.createFromResponse(item))
  }

  async fetchEventsData(start: Date, end: Date): Promise<void> {

    let payloadWithoutCancelToken: FilterEventsPayload = {
      before: end,
      after: start
    }

    let payloadWithCancelToken: FilterEventsWithCancelTokenPayload = {
      payload: payloadWithoutCancelToken,
      cancelToken: this.cancelToken
    }

    try {
      let response: CalendarEventForDay[] = await this.fetchCalendarEventsForDay(payloadWithCancelToken)
      this.events = response.map((e): CalendarEventForDay => ({
        id: e.id,
        kind: e.kind,
        date: moment(e.date).format('YYYY-MM-DD'),
      }))
    } catch (e) {
      if (axios.isCancel(e)) {
      } else {
        this.showSnackBar = true
        this.snackbarColor = 'error'
        this.snackBarMessage = this.$t('errors.fetchDataError').toString()
      }
    } finally {
    }
  }

  async fetchBusinesses(): Promise<void> {
    try {
      this.businessLoading = true
      this.businesses = await this.piniaStore.fetchBusinesses()

      if (this.businesses.length >= 1) {
        this.selectedBusiness = this.businesses[0]
      }

    } finally {
      this.businessLoading = false
    }
  }

  fixDateIfNeeded(): void {
    if (this.weeklyOfferCollection && this.date) {
      const offerDateCollection = this.weeklyOfferCollection.map((item: DailyMenuOffer) => item.date)

      if (!offerDateCollection.includes(this.date as unknown as Date)) {
        this.$router.replace({
          name: 'Dailymenu',
          params: {
            isoWeek: this.isoWeek,
            date: moment(_.first(offerDateCollection)).format('YYYY-MM-DD')
          }
        }).catch(() => {
        })
      }
    }
  }

  onBackToCalendarButtonClick(): void {
    this.$router.push({
      name: 'Dailymenu',
      params: {
        isoWeek: this.isoWeek
      }
    }).catch((e) => console.debug(e))
  }

  beforeRouteEnter(to: Route, from: Route, next: Function): void {
    redirectIfNeeded(to, next)
  }

  beforeRouteUpdate(to: Route, from: Route, next: Function): void {
    redirectIfNeeded(to, next)
  }

  async mounted() {
    await this.fetchBusinesses()

    this.fetchWeeklyData(this.isoWeek)
    this.fetchAllergens()
  }
}
</script>

<style lang="stylus" scoped>

.container {
  max-width: none
}

.menu {
  align-items: start !important
}

.calendar {
  width: 100%
}

.menu_detail {
  justify-items: start;
  width: 100%
}

@media screen and (max-width: 960px) {
  .menu {
    flex-direction: column
  }
}
</style>
